// src/Components/Promo/Broadcast.js

import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  LinearProgress,
  TableSortLabel,
  TextField,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useBroadcastManage } from '../../Hooks/useBroadcastManage';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

import {
  MenuAddNew,
  MenuAddNewGrey,
  MenuSave,
  MenuSaveGrey,
  MenuDelete,
  MenuDeleteGrey,
  MenuIconCancel,
  MenuDownload,
} from '../../assets/icons/CustomIcons';
import { MenuBroadcast } from '../../assets/icons/CustomIconsAdd2';

// Navigation Icons
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Page Indicator Icons
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';

// Background
import AdminBg from '../../assets/images/AdminBackground.png';

// --- CONSTANTS ---
const ORANGE_COLOR = '#FF9900';
const DARK_BLUE = '#00008B';
const LIGHT_BLUE_BG = '#E3F2FD';
const BLUE_COLOR = '#00B0FF';

// ============================================================================
// --- PAGE 2 IMPORT BUTTON CONTROL PANEL ---
// Adjust size, positioning, and colors for the Page 2 Import button here.
// ============================================================================
const IMPORT_BTN_STYLES = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '64px',
  },
  icon: (canImport) => ({
    fontSize: 28, // Size of the green tick
    color: canImport ? '#4caf50' : '#9e9e9e',
    opacity: canImport ? 1 : 0.5,
    mt: '6px', // Y-axis nudge for icon (positive = down, negative = up)
    ml: '0px', // X-axis nudge for icon (positive = right, negative = left)
    mb: '1px', // Gap between the icon and the text
  }),
  text: (canImport) => ({
    fontFamily: 'Arial',
    fontSize: '10px', // Size of the text
    fontWeight: 'bold',
    color: canImport ? '#333333' : '#9e9e9e',
    opacity: canImport ? 1 : 0.5,
    lineHeight: 1,
    mt: '0px', // Y-axis nudge for text
    ml: '0px', // X-axis nudge for text
  }),
  spinner: {
    color: '#4caf50',
    mt: '1px',
    ml: '0px',
    mb: '4px',
  },
};
// ============================================================================

// --- CUSTOM TRIANGLE ICON ---
const CustomSortIcon = ({ active, className }) => {
  return (
    <svg className={className} focusable='false' viewBox='0 0 24 24' aria-hidden='true'>
      {active ? (
        <path d='M12 8l6 8H6z' fill='currentColor' />
      ) : (
        <path
          d='M12 8l6 8H6z'
          fill='none'
          stroke='currentColor'
          strokeWidth='1'
          strokeLinejoin='round'
        />
      )}
    </svg>
  );
};

export default function Broadcast({ setIsDirty }) {
  const { state, flags, alerts, actions } = useBroadcastManage();

  const [focusedRowId, setFocusedRowId] = useState(null);

  const [timeRemainingStr, setTimeRemainingStr] = useState('00:00:00');
  const [progressPercent, setProgressPercent] = useState(0);

  // --- PAGINATION STATE ---
  const [viewMode, setViewMode] = useState('PAGE1'); // 'PAGE1' or 'PAGE2'
  const [showImportConfirm, setShowImportConfirm] = useState(false);

  // --- NEW ALERT STATE ---
  const [showWaitingImportAlert, setShowWaitingImportAlert] = useState(false);
  const hasShownWaitingAlertRef = useRef(false);
  const prevPage2CountRef = useRef(0);

  // ========================================================================
  // DYNAMIC PAGINATION LINK: Connected to sorted uploadMailingListBroadcast
  // ========================================================================
  const page2DataRows = state.displayPage2Rows || [];
  const hasPage2Content = page2DataRows.length > 0;

  // WAITING IMPORT ALERT LOGIC
  useEffect(() => {
    const currentCount = page2DataRows.length;
    if (viewMode === 'PAGE1') {
      if (currentCount > 0) {
        if (
          !hasShownWaitingAlertRef.current ||
          currentCount > prevPage2CountRef.current
        ) {
          setShowWaitingImportAlert(true);
          hasShownWaitingAlertRef.current = true;
        }
      }
    } else {
      hasShownWaitingAlertRef.current = false;
    }
    prevPage2CountRef.current = currentCount;
  }, [viewMode, page2DataRows.length]);

  // KICKBACK: If the user deletes the last row while on Page 2, bump them to Page 1.
  useEffect(() => {
    if (!hasPage2Content && viewMode === 'PAGE2') {
      setViewMode('PAGE1');
    }
  }, [hasPage2Content, viewMode]);

  useEffect(() => {
    if (setIsDirty) {
      setIsDirty(flags.isDirty);
    }
  }, [flags.isDirty, setIsDirty]);

  // --- TIME TICKER ENGINE ---
  useEffect(() => {
    const inProcessRow = state.displayRows.find((r) => r.status === 'In Process');

    if (!inProcessRow || !inProcessRow.date) {
      setTimeRemainingStr('00:00:00');
      setProgressPercent(0);
      return;
    }

    const delayParts = (state.delayConfigString || '48:00:00').split(':');
    const totalDelaySeconds =
      (parseInt(delayParts[0], 10) || 0) * 3600 +
      (parseInt(delayParts[1], 10) || 0) * 60 +
      (parseInt(delayParts[2], 10) || 0);

    const calculateProgress = () => {
      const startTime = new Date(inProcessRow.date).getTime();
      const now = Date.now();
      const elapsedSeconds = Math.max(0, Math.floor((now - startTime) / 1000));

      if (elapsedSeconds >= totalDelaySeconds) {
        setTimeRemainingStr('00:00:00');
        setProgressPercent(100);
        actions.handleDelayComplete(inProcessRow.id);
        return false;
      }

      const remainingSeconds = totalDelaySeconds - elapsedSeconds;

      const hrs = Math.floor(remainingSeconds / 3600)
        .toString()
        .padStart(2, '0');
      const mins = Math.floor((remainingSeconds % 3600) / 60)
        .toString()
        .padStart(2, '0');
      const secs = Math.floor(remainingSeconds % 60)
        .toString()
        .padStart(2, '0');

      setTimeRemainingStr(`${hrs}:${mins}:${secs}`);
      setProgressPercent((elapsedSeconds / totalDelaySeconds) * 100);
      return true;
    };

    const keepTicking = calculateProgress();
    if (!keepTicking) return;

    const timerId = setInterval(calculateProgress, 1000);
    return () => clearInterval(timerId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.displayRows, state.delayConfigString]);

  const ICON_SIZE = 64;
  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return '';
    if (typeof dateVal.toDate === 'function') {
      return dateVal.toDate().toLocaleDateString();
    }
    return new Date(dateVal).toLocaleDateString();
  };

  const headerSx = {
    backgroundColor: '#fff3e0',
    fontWeight: 'bold',
    borderBottom: '2px solid #ff9800',
    py: '4px',
    textAlign: 'center',
    color: 'black',
  };

  const sortLabelSx = {
    '&.MuiTableSortLabel-root': { color: 'inherit' },
    '&.MuiTableSortLabel-root:hover': {
      color: 'inherit',
      '& .MuiTableSortLabel-icon': { opacity: 0.6 },
    },
    '& .MuiTableSortLabel-icon': {
      opacity: 0.4,
      width: '1.5em',
      height: '1.5em',
      color: '#000000',
      transition: 'opacity 0.2s, color 0.2s',
    },
    '&.Mui-active': { color: 'inherit' },
    '&.Mui-active .MuiTableSortLabel-icon': { opacity: 1, color: '#000000' },
  };

  const portalOverlay = createPortal(
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        bgcolor: 'rgba(255,255,255,0.3)',
        zIndex: 100002,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'wait',
      }}>
      <CircularProgress sx={{ color: '#00B0FF' }} size={60} thickness={4} />
    </Box>,
    document.body,
  );

  const renderProgressBar = state.displayRows.some((r) => r.status === 'In Process');

  // AMENDED: Check if system is locked (either broadcasting or waiting on delay)
  const isLocked = state.isBroadcasting || renderProgressBar;

  const disableAddNew = viewMode !== 'PAGE1';

  const disableDelete =
    isLocked ||
    (viewMode === 'PAGE1' ? !flags.canDelete : state.page2SelectedIds.length === 0);

  const disableDownload =
    viewMode === 'PAGE1'
      ? state.displayRows.length === 0 || flags.canSave || isLocked
      : page2DataRows.length === 0 || isLocked;

  const disableNavBack = viewMode === 'PAGE1';
  const disableNavForward = viewMode === 'PAGE2' || !hasPage2Content;

  const validSelectedCount = state.page2SelectedIds.filter((id) => {
    const row = page2DataRows.find((r) => r.id === id);
    return row && ['Ok', 'Tick', 'Success', 'Complete', 'Completed'].includes(row.status);
  }).length;

  const hasSelection = state.page2SelectedIds.length > 0;
  const canImport = hasSelection && !isLocked;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {state.isBroadcasting && portalOverlay}

      {/* --- TOOLBAR --- */}
      <Box
        sx={{
          height: '60px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 50,
          px: '5px',
        }}>
        {/* ADD NEW */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={actions.handleAddNew}
            disabled={disableAddNew || isLocked}>
            {disableAddNew || isLocked ? (
              <MenuAddNewGrey sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuAddNew sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        {/* BROADCAST OR UPLOAD DYNAMICALLY */}
        <Box sx={toolBlockStyle}>
          {viewMode === 'PAGE1' ? (
            <IconButton
              sx={{ p: 0, transform: 'translateY(-5px)' }}
              onClick={actions.handleBroadcastRequest}
              disabled={!flags.canBroadcast || isLocked}>
              <MenuBroadcast
                sx={{
                  fontSize: ICON_SIZE,
                  opacity: flags.canBroadcast && !isLocked ? 1 : 0.35,
                  filter: flags.canBroadcast && !isLocked ? 'none' : 'grayscale(100%)',
                }}
                iconScale={5}
                iconX={19}
                iconY={39}
                textY={108}
              />
            </IconButton>
          ) : (
            <IconButton
              sx={{ p: 0, transform: 'translateY(-2px)' }}
              disabled={state.page2SelectedIds.length === 0 || isLocked}
              onClick={() => setShowImportConfirm(true)}>
              <Box sx={IMPORT_BTN_STYLES.container}>
                {state.loading ? (
                  <CircularProgress size={34} sx={IMPORT_BTN_STYLES.spinner} />
                ) : (
                  <CheckCircleIcon sx={IMPORT_BTN_STYLES.icon(canImport)} />
                )}
                <Typography sx={IMPORT_BTN_STYLES.text(canImport)}>Import</Typography>
              </Box>
            </IconButton>
          )}
        </Box>

        {/* DOWNLOAD */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => actions.executeDownload(viewMode)}
            disabled={disableDownload || isLocked}>
            <MenuDownload
              sx={{
                fontSize: ICON_SIZE,
                opacity: disableDownload || isLocked ? 0.35 : 1,
                filter: disableDownload || isLocked ? 'grayscale(100%)' : 'none',
              }}
              iconScale={5}
              iconX={32}
              iconY={38}
              textY={108}
            />
          </IconButton>
        </Box>

        {/* SAVE */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={actions.handleSave}
            disabled={!flags.canSave || isLocked}>
            {flags.canSave && !isLocked ? (
              <MenuSave sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        {/* DELETE */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => actions.handleDeleteRequest(viewMode)}
            disabled={disableDelete}>
            {disableDelete ? (
              <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuDelete sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        {/* CANCEL */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => {
              if (flags.isDirty) {
                alerts.setShowUnsavedAlert(true);
              } else {
                actions.resetInterface();
                setViewMode('PAGE1');
              }
            }}
            disabled={isLocked}>
            <MenuIconCancel
              sx={{
                fontSize: ICON_SIZE,
                opacity: isLocked ? 0.35 : 1,
                filter: isLocked ? 'grayscale(100%)' : 'none',
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* --- CONTENT AREA --- */}
      <Box
        sx={{
          flexGrow: 1,
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
        }}>
        {state.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : viewMode === 'PAGE1' ? (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              height: '100%',
              width: '100%',
              boxSizing: 'border-box',
              borderTop: '2px solid #ff9800',
              borderBottom: 'none',
              borderRadius: 0,
              overflowY: 'auto',
            }}>
            <Table stickyHeader size='small' sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ height: '36px' }}>
                  <TableCell
                    sx={{ width: '40px', textAlign: 'center', p: 0, ...headerSx }}>
                    <Checkbox
                      color='primary'
                      indeterminate={
                        state.selectedIds.length > 0 &&
                        state.selectedIds.length < state.displayRows.length
                      }
                      checked={
                        state.displayRows.length > 0 &&
                        state.selectedIds.length === state.displayRows.length
                      }
                      onChange={(e) => actions.handleSelectAll(e.target.checked)}
                      size='small'
                      sx={{ p: 0.5 }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '20%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    Description
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '10%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    Date
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '25%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    Newsfeed Name
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '10%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    Import List
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '10%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    Mailing List
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '25%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.displayRows.map((row) => {
                  const isSelected = state.selectedIds.includes(row.id);
                  const isNewRow = row.id && row.id.startsWith('temp_');
                  const isFocusedRow = focusedRowId === row.id;

                  const isEditable = isNewRow || !row.status || row.status.trim() === '';

                  const validStatuses = [
                    'Ok',
                    'Tick',
                    'Success',
                    'Complete',
                    'Completed',
                  ];
                  const errorStatuses = [
                    'Unsubscribed',
                    'Bounce Back',
                    'Hard Bounce',
                    'Soft Bounce',
                    'Spam Complaint',
                  ];

                  const renderStatus = () => {
                    if (validStatuses.includes(row.status)) {
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            px: 1,
                            minHeight: '20px',
                          }}>
                          <CheckCircleIcon
                            sx={{
                              color: 'green',
                              fontSize: '1.2rem',
                            }}
                          />
                        </Box>
                      );
                    }
                    if (errorStatuses.includes(row.status)) {
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            px: 1,
                            minHeight: '20px',
                            gap: 0.5,
                          }}>
                          <CancelIcon
                            sx={{ color: '#d32f2f', fontSize: '1.2rem', flexShrink: 0 }}
                          />
                          <Typography
                            variant='body2'
                            sx={{
                              color: 'black',
                              fontSize: '0.8rem',
                              fontWeight: 'normal',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              textAlign: 'left',
                            }}
                            title={row.status}>
                            {row.status}
                          </Typography>
                        </Box>
                      );
                    }
                    if (!row.status || row.status.trim() === '') {
                      return null;
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          px: 1,
                          minHeight: '20px',
                        }}>
                        <Typography
                          variant='body2'
                          sx={{
                            color: 'black',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            textAlign: 'left',
                          }}
                          title={row.status}>
                          {row.status}
                        </Typography>
                      </Box>
                    );
                  };

                  return (
                    <TableRow
                      id={`row-${row.id}`}
                      key={row.id}
                      hover
                      onClick={() => setFocusedRowId(row.id)}
                      sx={{
                        height: '32px',
                        backgroundColor: isFocusedRow
                          ? 'rgba(0, 176, 255, 0.15) !important'
                          : 'inherit',
                        transition: 'background-color 0.5s ease',
                        '& td': { py: '4px' },
                        '&.Mui-selected': { backgroundColor: 'transparent !important' },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                        },
                      }}>
                      <TableCell
                        sx={{ textAlign: 'center', p: 0, cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          actions.handleCheckboxChange(row.id);
                        }}>
                        <Checkbox
                          checked={isSelected}
                          size='small'
                          sx={{ pointerEvents: 'none', p: 0.5 }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{ px: 1, maxWidth: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFocusedRowId(row.id);
                        }}>
                        {isEditable ? (
                          <TextField
                            value={row.description || ''}
                            onChange={(e) =>
                              actions.handleFieldChange(
                                row.id,
                                'description',
                                e.target.value,
                                e,
                              )
                            }
                            variant='standard'
                            size='small'
                            fullWidth
                            InputProps={{
                              disableUnderline: true,
                            }}
                            sx={{
                              backgroundColor: LIGHT_BLUE_BG,
                              px: 1,
                              borderRadius: 1,
                              border: `1px solid ${ORANGE_COLOR}`,
                              height: '24px',
                              '& .MuiInputBase-root': {
                                height: '100%',
                                fontSize: '0.8rem',
                                color: DARK_BLUE,
                              },
                              '& .MuiInputBase-input': {
                                py: 0,
                              },
                            }}
                          />
                        ) : (
                          <Typography
                            variant='body2'
                            sx={{
                              color: 'black',
                              fontSize: '0.8rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              px: 1,
                            }}>
                            {row.description || ''}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ maxWidth: 0, textAlign: 'center' }}>
                        <Typography
                          variant='body2'
                          sx={{
                            color: 'black',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                          {isNewRow ? 'New' : formatDate(row.date)}
                        </Typography>
                      </TableCell>

                      <TableCell
                        sx={{ px: 1, maxWidth: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFocusedRowId(row.id);
                        }}>
                        {isEditable ? (
                          <Select
                            value={row.newsfeedName || ''}
                            onChange={(e) =>
                              actions.handleFieldChange(
                                row.id,
                                'newsfeedName',
                                e.target.value,
                              )
                            }
                            displayEmpty
                            size='small'
                            fullWidth
                            variant='standard'
                            disableUnderline
                            MenuProps={{ sx: { zIndex: 100005 } }}
                            sx={{
                              backgroundColor: LIGHT_BLUE_BG,
                              px: 1,
                              borderRadius: 1,
                              border: `1px solid ${ORANGE_COLOR}`,
                              fontSize: '0.8rem',
                              color: DARK_BLUE,
                              height: '24px',
                              '& .MuiSelect-select': {
                                py: 0,
                                minHeight: '24px',
                                display: 'flex',
                                alignItems: 'center',
                              },
                            }}>
                            <MenuItem value='' disabled sx={{ fontSize: '0.8rem' }}>
                              Select Newsfeed...
                            </MenuItem>
                            {state.newsfeedOptions && state.newsfeedOptions.length > 0 ? (
                              state.newsfeedOptions.map((nf) => (
                                <MenuItem
                                  key={nf.id}
                                  value={nf.name || `Unnamed (${nf.id})`}
                                  sx={{ fontSize: '0.8rem' }}>
                                  {nf.name || `Unnamed (${nf.id})`}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem value='' disabled sx={{ fontSize: '0.8rem' }}>
                                No newsfeeds available
                              </MenuItem>
                            )}
                          </Select>
                        ) : (
                          <Typography
                            variant='body2'
                            sx={{
                              color: 'black',
                              fontSize: '0.8rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              px: 1,
                            }}>
                            {row.newsfeedName || ''}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell
                        sx={{ textAlign: 'center', p: 0 }}
                        onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={!!row.isImportList}
                          onChange={(e) =>
                            actions.handleFieldChange(
                              row.id,
                              'isImportList',
                              e.target.checked,
                            )
                          }
                          disabled={!isEditable}
                          size='small'
                          sx={{ p: 0.5 }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{ textAlign: 'center', p: 0 }}
                        onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={!!row.isMailingList}
                          onChange={(e) =>
                            actions.handleFieldChange(
                              row.id,
                              'isMailingList',
                              e.target.checked,
                            )
                          }
                          disabled={!isEditable}
                          size='small'
                          sx={{ p: 0.5 }}
                        />
                      </TableCell>

                      <TableCell sx={{ px: 1, maxWidth: 0 }}>{renderStatus()}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : viewMode === 'PAGE2' ? (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              height: '100%',
              width: '100%',
              boxSizing: 'border-box',
              borderTop: '2px solid #ff9800',
              borderBottom: 'none',
              borderRadius: 0,
              overflowY: 'auto',
            }}>
            <Table stickyHeader size='small' sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ height: '36px' }}>
                  <TableCell
                    sx={{ width: '40px', textAlign: 'center', p: 0, ...headerSx }}>
                    <Checkbox
                      color='primary'
                      indeterminate={
                        state.page2SelectedIds.length > 0 &&
                        state.page2SelectedIds.length < page2DataRows.length
                      }
                      checked={
                        page2DataRows.length > 0 &&
                        state.page2SelectedIds.length === page2DataRows.length
                      }
                      onChange={(e) => actions.handlePage2SelectAll(e.target.checked)}
                      size='small'
                      sx={{ p: 0.5 }}
                    />
                  </TableCell>

                  {/* DATE COLUMN */}
                  <TableCell
                    sx={{
                      width: '10%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.page2SortConfig.key === 'addedAt'}
                      direction={
                        state.page2SortConfig.key === 'addedAt'
                          ? state.page2SortConfig.direction
                          : 'asc'
                      }
                      onClick={() => actions.requestPage2Sort('addedAt')}
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.page2SortConfig.key === 'addedAt'}
                        />
                      )}
                      sx={sortLabelSx}>
                      Date
                    </TableSortLabel>
                  </TableCell>

                  {/* USER NAME COLUMN */}
                  <TableCell
                    sx={{
                      width: '20%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.page2SortConfig.key === 'userName'}
                      direction={
                        state.page2SortConfig.key === 'userName'
                          ? state.page2SortConfig.direction
                          : 'asc'
                      }
                      onClick={() => actions.requestPage2Sort('userName')}
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.page2SortConfig.key === 'userName'}
                        />
                      )}
                      sx={sortLabelSx}>
                      User Name
                    </TableSortLabel>
                  </TableCell>

                  {/* COMPANY COLUMN */}
                  <TableCell
                    sx={{
                      width: '20%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.page2SortConfig.key === 'companyName'}
                      direction={
                        state.page2SortConfig.key === 'companyName'
                          ? state.page2SortConfig.direction
                          : 'asc'
                      }
                      onClick={() => actions.requestPage2Sort('companyName')}
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.page2SortConfig.key === 'companyName'}
                        />
                      )}
                      sx={sortLabelSx}>
                      Company
                    </TableSortLabel>
                  </TableCell>

                  {/* EMAIL COLUMN */}
                  <TableCell
                    sx={{
                      width: '25%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.page2SortConfig.key === 'email'}
                      direction={
                        state.page2SortConfig.key === 'email'
                          ? state.page2SortConfig.direction
                          : 'asc'
                      }
                      onClick={() => actions.requestPage2Sort('email')}
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.page2SortConfig.key === 'email'}
                        />
                      )}
                      sx={sortLabelSx}>
                      Email
                    </TableSortLabel>
                  </TableCell>

                  {/* STATUS COLUMN */}
                  <TableCell
                    sx={{
                      width: '20%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.page2SortConfig.key === 'status'}
                      direction={
                        state.page2SortConfig.key === 'status'
                          ? state.page2SortConfig.direction
                          : 'asc'
                      }
                      onClick={() => actions.requestPage2Sort('status')}
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.page2SortConfig.key === 'status'}
                        />
                      )}
                      sx={sortLabelSx}>
                      Status
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {page2DataRows.map((row) => {
                  const isSelected = state.page2SelectedIds.includes(row.id);

                  const validStatuses = [
                    'Ok',
                    'Tick',
                    'Success',
                    'Complete',
                    'Completed',
                  ];
                  const errorStatuses = [
                    'Unsubscribed',
                    'Bounce Back',
                    'Hard Bounce',
                    'Soft Bounce',
                    'Spam Complaint',
                  ];

                  return (
                    <TableRow
                      key={row.id}
                      hover
                      selected={isSelected}
                      onClick={() => actions.handlePage2CheckboxChange(row.id)}
                      sx={{
                        height: '32px',
                        cursor: 'pointer',
                        transition: 'background-color 0.5s ease',
                        '& td': { py: '4px' },
                        '&.Mui-selected': { backgroundColor: 'transparent !important' },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                        },
                      }}>
                      <TableCell sx={{ textAlign: 'center', p: 0 }}>
                        <Checkbox
                          checked={isSelected}
                          size='small'
                          sx={{ pointerEvents: 'none', p: 0.5 }}
                        />
                      </TableCell>

                      <TableCell sx={{ maxWidth: 0, textAlign: 'center' }}>
                        <Typography
                          variant='body2'
                          sx={{
                            color: 'black',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={formatDate(row.addedAt || row.requestDate)}>
                          {formatDate(row.addedAt || row.requestDate)}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ px: 1, maxWidth: 0 }}>
                        <Typography
                          variant='body2'
                          sx={{
                            color: 'black',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={row.userName}>
                          {row.userName}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ px: 1, maxWidth: 0 }}>
                        <Typography
                          variant='body2'
                          sx={{
                            color: 'black',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={row.companyName}>
                          {row.companyName}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ px: 1, maxWidth: 0 }}>
                        <Typography
                          variant='body2'
                          sx={{
                            color: 'black',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={row.email}>
                          {row.email}
                        </Typography>
                      </TableCell>

                      {/* AMENDED: Identical styling to UploadMailingList EditableCell */}
                      <TableCell sx={{ px: 1, maxWidth: 0 }}>
                        {validStatuses.includes(row.status) ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              px: 1,
                              minHeight: '20px',
                            }}>
                            <CheckCircleIcon
                              sx={{
                                color: 'green',
                                fontSize: '1.2rem',
                              }}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              px: 1,
                              minHeight: '20px',
                              gap: 0.5,
                            }}>
                            {errorStatuses.includes(row.status) && (
                              <CancelIcon
                                sx={{
                                  color: '#d32f2f',
                                  fontSize: '1.2rem',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <Typography
                              variant='body2'
                              sx={{
                                color: 'black',
                                fontSize: '0.8rem',
                                fontWeight: 'normal',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                textAlign: 'left',
                              }}
                              title={row.status}>
                              {row.status || ''}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Box>

      {/* --- PROGRESS BAR SPANNING ENTIRE TOP OF FOOTER --- */}
      {renderProgressBar && (
        <Box sx={{ width: '100%', position: 'relative' }}>
          <LinearProgress
            variant='determinate'
            value={progressPercent}
            sx={{
              height: 5,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#4caf50',
              },
            }}
          />
          {[10, 20, 30, 40, 50, 60, 70, 80, 90].map((mark) => (
            <Box
              key={mark}
              sx={{
                position: 'absolute',
                left: `${mark}%`,
                top: 0,
                width: '1px',
                height: '5px',
                backgroundColor: '#999',
                zIndex: 2,
              }}
              title={`${mark}% Mark`}
            />
          ))}
        </Box>
      )}

      {/* --- FOOTER NAVIGATION --- */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 3,
          py: 0.5,
          minHeight: '40px',
          borderTop: renderProgressBar ? 'none' : '1px solid black',
          backgroundImage: `linear-gradient(rgba(184, 231, 253, .85), rgba(184, 231, 253, .85)), url(${AdminBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          flexShrink: 0,
          gap: 1,
          overflow: 'visible',
          position: 'relative',
        }}>
        {/* TIMER EMBEDDED INSIDE FOOTER AT 25% LEFT */}
        {renderProgressBar && (
          <Typography
            variant='body2'
            sx={{
              position: 'absolute',
              left: '25%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              color: DARK_BLUE,
              backgroundColor: LIGHT_BLUE_BG,
              px: 1.5,
              py: 0.25,
              borderRadius: 1,
              border: `1px solid ${BLUE_COLOR}`,
              zIndex: 5,
            }}>
            Time Remaining: {timeRemainingStr}
          </Typography>
        )}

        {/* PAGE INDICATOR DOTS */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            gap: 1,
          }}>
          {viewMode === 'PAGE1' ? (
            <CircleIcon sx={{ fontSize: '14px', color: '#0055ff' }} />
          ) : (
            <CircleOutlinedIcon sx={{ fontSize: '14px', color: '#666' }} />
          )}

          {hasPage2Content &&
            (viewMode === 'PAGE2' ? (
              <CircleIcon sx={{ fontSize: '14px', color: '#0055ff' }} />
            ) : (
              <CircleOutlinedIcon sx={{ fontSize: '14px', color: '#666' }} />
            ))}
        </Box>

        {/* NAVIGATION ARROWS */}
        <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => setViewMode('PAGE1')}
            disabled={disableNavBack}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <ArrowBackIosNewIcon
              sx={{
                color: disableNavBack ? 'grey' : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>

          <IconButton
            onClick={() => setViewMode('PAGE2')}
            disabled={disableNavForward}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <ArrowForwardIosIcon
              sx={{
                color: disableNavForward ? 'grey' : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* --- DIALOGS --- */}
      <ConfirmDialog
        open={showWaitingImportAlert}
        onClose={() => setShowWaitingImportAlert(false)}
        onConfirm={() => setShowWaitingImportAlert(false)}
        message='There are users on the next page waiting to be imported to the mailing list'
        severity='info'
        confirmText='OK'
        hideCancel={true}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts.showUnsavedAlert}
        onClose={() => alerts.setShowUnsavedAlert(false)}
        onConfirm={() => {
          alerts.setShowUnsavedAlert(false);
          actions.resetInterface();
          setViewMode('PAGE1');
        }}
        message='Cancel without Save will discard your changes?'
        severity='warning'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts.showDeleteAlert}
        onClose={() => alerts.setShowDeleteAlert(false)}
        onConfirm={actions.handleConfirmDelete}
        message={
          state.deleteTarget === 'PAGE2'
            ? 'Remove selected archive records?'
            : 'Remove selected broadcast campaigns?'
        }
        severity='error'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts.showBroadcastConfirm}
        onClose={() => alerts.setShowBroadcastConfirm(false)}
        onConfirm={actions.executeBroadcast}
        message={`This will broadcast "${alerts.broadcastDetails.newsfeedName}" to ${alerts.broadcastDetails.targetCount} users?`}
        severity='warning'
        confirmText='Yes, Broadcast'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts.showErrorAlert}
        onClose={() => alerts.setShowErrorAlert(false)}
        onConfirm={() => alerts.setShowErrorAlert(false)}
        hideCancel={true}
        confirmText='OK'
        message={alerts.errorMessage}
        severity='error'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts.showProgressAlert}
        onClose={() => alerts.setShowProgressAlert(false)}
        message={alerts.progressMessage}
        severity={alerts.progressSeverity}
        autoClose={state.isBroadcasting ? 0 : 3000}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />
      <ConfirmDialog
        open={showImportConfirm}
        onClose={() => setShowImportConfirm(false)}
        onConfirm={() => {
          setShowImportConfirm(false);
          actions.handleUploadToMailingList();
        }}
        message={`${validSelectedCount} valid users will be imported to the Mailing List`}
        severity='warning'
        confirmText='OK'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />
    </Box>
  );
}
